import api from './api';
import { ApiResponse } from '../types';
import {
  FeeInvoiceRow,
  FeeInvoiceDetail,
  FeeInvoiceListFilter,
  FeeInvoiceListResponse,
  GeneratePreview,
  GenerateResult,
  GenerateInput,
  FeeVoucherPrintSet,
} from '../types/fees';

const buildListParams = (filter: FeeInvoiceListFilter = {}) => {
  const sp = new URLSearchParams();
  if (filter.sessionId)         sp.append('sessionId', filter.sessionId);
  if (filter.classId)           sp.append('classId', filter.classId);
  if (filter.sectionInstanceId === null)   sp.append('sectionInstanceId', 'null');
  else if (filter.sectionInstanceId)       sp.append('sectionInstanceId', filter.sectionInstanceId);
  if (filter.studentId)         sp.append('studentId', filter.studentId);
  if (filter.feePlanId)         sp.append('feePlanId', filter.feePlanId);
  if (filter.feeAssignmentId)   sp.append('feeAssignmentId', filter.feeAssignmentId);
  if (filter.status)            sp.append('status', filter.status);
  if (filter.view)              sp.append('view', filter.view);
  if (filter.periodLabel)       sp.append('periodLabel', filter.periodLabel);
  if (filter.dueBefore)         sp.append('dueBefore', filter.dueBefore);
  if (filter.dueAfter)          sp.append('dueAfter', filter.dueAfter);
  if (filter.search)            sp.append('search', filter.search);
  if (filter.grNumber)       sp.append('grNumber', filter.grNumber);
  if (filter.page)              sp.append('page', String(filter.page));
  if (filter.limit)             sp.append('limit', String(filter.limit));
  return sp;
};

export const listFeeInvoices = async (filter: FeeInvoiceListFilter = {}): Promise<FeeInvoiceListResponse> => {
  const sp = buildListParams(filter);
  const response = await api.get<ApiResponse<FeeInvoiceListResponse>>(`/fee-invoices?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getFeeInvoice = async (id: string): Promise<FeeInvoiceDetail> => {
  const response = await api.get<ApiResponse<FeeInvoiceDetail>>(`/fee-invoices/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

/** An applicant's challan as the admissions drawer shows it. */
export type ApplicationChallan = Pick<FeeInvoiceDetail,
  '_id' | 'invoiceNumber' | 'kind' | 'status' | 'totals' | 'dueDate' | 'issuedAt' | 'period' | 'lineItems' | 'cancellationReason'>;

/** An applicant's challans and what is still owed on them — one request. */
export const listApplicationInvoices = async (
  applicationId: string,
): Promise<{ invoices: ApplicationChallan[]; totals: { outstanding: number } }> => {
  const response = await api.get<ApiResponse<{ invoices: ApplicationChallan[]; totals: { outstanding: number } }>>(
    `/fee-invoices/by-application/${applicationId}`,
  );
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const previewGenerate = async (input: GenerateInput): Promise<GeneratePreview> => {
  const response = await api.post<ApiResponse<GeneratePreview>>('/fee-invoices/preview-generate', input);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const generateInvoices = async (input: GenerateInput): Promise<GenerateResult> => {
  const response = await api.post<ApiResponse<GenerateResult>>('/fee-invoices/generate', input);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

// ─── Late fees ───────────────────────────────────────────────────────────────

export interface LateFeeScope {
  asOfDate?: string | null;
  sessionId?: string | null;
  classId?: string | null;
}

export const previewApplyLateFees = async (scope: LateFeeScope = {}): Promise<import('../types/fees').LateFeePreview> => {
  const response = await api.post<ApiResponse<import('../types/fees').LateFeePreview>>('/fee-invoices/preview-late-fees', scope);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const applyLateFees = async (scope: LateFeeScope = {}): Promise<import('../types/fees').LateFeeApplyResult> => {
  const response = await api.post<ApiResponse<import('../types/fees').LateFeeApplyResult>>('/fee-invoices/apply-late-fees', scope);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

// ─── Adjustments ─────────────────────────────────────────────────────────────

export const listAdjustments = async (invoiceId: string): Promise<import('../types/fees').FeeAdjustment[]> => {
  const response = await api.get<ApiResponse<{ adjustments: import('../types/fees').FeeAdjustment[] }>>(`/fee-invoices/${invoiceId}/adjustments`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!.adjustments;
};

export interface CreateAdjustmentInput {
  kind: 'ad_hoc_charge' | 'correction' | 'other';
  amount: number;
  reason: string;
}

export const createAdjustment = async (invoiceId: string, input: CreateAdjustmentInput): Promise<import('../types/fees').FeeAdjustment> => {
  const response = await api.post<ApiResponse<import('../types/fees').FeeAdjustment>>(`/fee-invoices/${invoiceId}/adjustments`, input);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const revokeAdjustment = async (invoiceId: string, adjustmentId: string, reason?: string): Promise<import('../types/fees').FeeAdjustment> => {
  const response = await api.post<ApiResponse<import('../types/fees').FeeAdjustment>>(`/fee-invoices/${invoiceId}/adjustments/${adjustmentId}/revoke`, { reason });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const cancelInvoice = async (id: string, reason?: string): Promise<FeeInvoiceRow> => {
  const response = await api.post<ApiResponse<FeeInvoiceRow>>(`/fee-invoices/${id}/cancel`, { reason });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

/** One challan's voucher, for the PDF drawn in the browser. */
export const getInvoicePrint = async (id: string): Promise<FeeVoucherPrintSet> => {
  const response = await api.get<ApiResponse<FeeVoucherPrintSet>>(`/fee-invoices/${id}/print`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

/** Up to 200 challans' vouchers for one pack, in hand-out order (class, section, roll). */
export const printInvoices = async (invoiceIds: string[]): Promise<FeeVoucherPrintSet> => {
  const response = await api.post<ApiResponse<FeeVoucherPrintSet>>('/fee-invoices/print', { invoiceIds });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

const feeInvoiceService = {
  listFeeInvoices,
  getFeeInvoice,
  previewGenerate,
  generateInvoices,
  cancelInvoice,
  getInvoicePrint,
  printInvoices,
  previewApplyLateFees,
  applyLateFees,
  listAdjustments,
  createAdjustment,
  revokeAdjustment,
};

export default feeInvoiceService;
